import {
  $getRoot,
  $isElementNode,
  $isParagraphNode,
  type ElementNode,
  type LexicalEditor,
  type LexicalNode,
  type ParagraphNode,
  type RootNode,
  type TextNode,
} from "lexical";

import {
  type IMjmlButtonProps,
  type IMjmlColumnProps,
  MjmlButton,
  MjmlColumn,
  MjmlDivider,
  MjmlGroup,
  MjmlRaw,
  MjmlSection,
  MjmlTable,
  MjmlText,
  MjmlWrapper,
} from "@faire/mjml-react";
import { $isListNode, ListItemNode, ListNode } from "@lexical/list";
import {
  $isDecoratorBlockNode,
  DecoratorBlockNode,
} from "@lexical/react/LexicalDecoratorBlockNode";
import { $isHeadingNode, HeadingNode } from "@lexical/rich-text";
import {
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellNode,
  TableNode,
} from "@lexical/table";
import { Fragment } from "react";
import ReactDOMServer from "react-dom/server";
import type { UUID } from "../../../../../utils/uuid";
import type { EmailDesign } from "../../../../email-design/server/supabase-email-design-service";
import { ConditionalWrapper } from "../../../../generic/components/conditional-wrapper";
import type { PollWithOptions } from "../../../../poll/server/supabase-poll-service";
import { $isDataTableCellNode } from "../../nodes/data-table-cell-node";
import { $isDataTableNode, DataTableNode } from "../../nodes/data-table-node";
import { $isDesignHuddleNode, DesignHuddleNode } from "../../nodes/design-huddle-node";
import { $isDividerNode, DividerNode } from "../../nodes/divider-node";
import {
  $isDynamicContentNode,
  DynamicContentNode,
} from "../../nodes/dynamic-content/dynamic-content-node";
import { $isDynamicContentToolbarNode } from "../../nodes/dynamic-content/helpers";
import { $isImageNode, ImageNode } from "../../nodes/image-node";
import { $getPaperColorNode, $isPaperColorNode } from "../../nodes/paper-color-node";
import { $isPollNode } from "../../nodes/poll/helpers";
import { PollNode } from "../../nodes/poll/poll-node";
import { $isStylableButtonNode, StylableButtonNode } from "../../nodes/stylable-button-node";
import { $isStylableTableCellNode } from "../../nodes/stylable-table-cell-node";
import { $isVideoNode, VideoNode } from "../../nodes/video-node";
import type { PollSchema } from "../../plugins/poll-plugin/types";
import type { SerialisedEditorDesign } from "../../types/serialised-editor-design";
import { createConfiguredEditor } from "../headless-editor";
import { calculateButtonWidthForPoll } from "./calculate-button-width-for-poll";
import { exportHtml } from "./export-html";
import { HorizontalPollButtons } from "./poll-buttons/custom/horizontal";
import { VerticalPollButtons } from "./poll-buttons/custom/vertical";
import { StandardPollButtons } from "./poll-buttons/standard";
type BodyClassName = "paper" | "header" | "footer";

export function convertToMjmlString(
  content: SerialisedEditorDesign,
  account_id: UUID,
  className: BodyClassName,
  paperSize?: number | null,
  notificationPreview: boolean = false,
): string {
  if (content.type === "markdown") throw new Error("Must edit first.");
  const editor = createConfiguredEditor({ format: "email" });

  editor.setEditorState(editor.parseEditorState(content));

  return editor
    .getEditorState()
    .read(() =>
      $convertToMjmlString(
        editor,
        account_id,
        className,
        paperSize,
        {},
        undefined,
        notificationPreview,
      ),
    );
}

export function $convertToMjmlString(
  editor: LexicalEditor,
  account_id: UUID,
  className: BodyClassName,
  paperSize?: number | null,
  pollsData: { [key: string]: PollWithOptions } = {},
  emailDesign?: EmailDesign,
  notificationPreview: boolean = false,
): string {
  const paperColorNode = $getPaperColorNode();
  const extraProps = paperColorNode ? { backgroundColor: paperColorNode.__hex } : {};

  const data = ReactDOMServer.renderToString(
    <MjmlWrapper cssClass={className} mjmlClass={className} {...extraProps}>
      {notificationPreview && (
        <MjmlSection mjmlClass="section">
          <MjmlColumn>
            <MjmlText line-height="0px" padding="0px">
              {Array.from({ length: 100 }).map((_, i) => (
                <div style={{ display: "none" }} key={i}>
                  &#8199;&#65279;&#847;
                </div>
              ))}
            </MjmlText>
          </MjmlColumn>
        </MjmlSection>
      )}
      {transformRoot(
        $getRoot(),
        editor,
        account_id,
        className,
        paperSize ?? undefined,
        emailDesign,
        pollsData,
      )}
    </MjmlWrapper>,
  );
  return data;
}

const transformRoot = (
  node: RootNode,
  editor: LexicalEditor,
  account_id: UUID,
  className: BodyClassName,
  paperSize?: number,
  emailDesign?: EmailDesign,
  pollsData: { [key: string]: PollWithOptions } = {},
): React.JSX.Element[] => {
  // If we have any tables, they are considered to be a "row", so we need to group correctly.
  // e.g if node.getChildren() s [paragraph, paragraph, paragraph, table, paragraph]
  // we want to return [[paragraph, paragraph, paragraph], [table], [paragraph]]
  const groups = node
    .getChildren()
    .reduce<LexicalNode[][]>(
      (acc, x) => {
        if ($isTableNode(x) || $isPollNode(x)) {
          acc.push([x]);
          acc.push([]);
          return acc;
        }

        const lastGroup = acc[acc.length - 1]!;
        lastGroup.push(x);
        return acc;
      },
      [[]],
    )
    .filter((x) => x.length > 0);

  const toElements = (group: LexicalNode[]): (React.JSX.Element | React.JSX.Element[])[] =>
    group
      .filter(
        (x): x is DecoratorBlockNode | ElementNode => $isDecoratorBlockNode(x) || $isElementNode(x),
      )
      .map((x) =>
        transfomTopLevel(x, editor, account_id, className, paperSize, emailDesign, pollsData),
      );

  return groups.map((group, i) => {
    const children = toElements(group);
    const last = children.at(-1);
    if (last instanceof Element) last.className = "last";

    if ($isPollNode(group[0])) return <Fragment key={`poll-${i}`}>{children}</Fragment>;

    return (
      <MjmlSection mjmlClass="section" key={`section-${i}`}>
        {$isTableNode(group[0]) ? (
          children
        ) : (
          <MjmlColumn key={`column-${i}`}>{children}</MjmlColumn>
        )}
      </MjmlSection>
    );
  });
};

const transfomTopLevel = (
  node: LexicalNode,
  editor: LexicalEditor,
  account_id: UUID,
  className: BodyClassName,
  paperSize?: number,
  emailDesign?: EmailDesign,
  pollsData: { [key: string]: PollWithOptions } = {},
  columnCount: number = 1,
): React.JSX.Element | React.JSX.Element[] => {
  if ($isParagraphNode(node) || $isHeadingNode(node)) return transformText(node, editor);

  if ($isListNode(node)) return transformList(node, editor);

  if ($isImageNode(node)) return transformImage(node, columnCount, paperSize);

  if ($isStylableButtonNode(node)) return transformButton(node);

  if ($isDividerNode(node)) return transformDividerNode(node);

  if ($isDesignHuddleNode(node)) return transformDesignHuddle(node, columnCount, paperSize);

  if ($isDataTableNode(node)) return transformDataTableNode(node, editor);

  if ($isTableNode(node))
    return transformTableNode(
      node,
      editor,
      account_id,
      className,
      emailDesign,
      pollsData,
      paperSize,
    );

  if ($isPaperColorNode(node)) return [];

  if ($isDynamicContentToolbarNode(node)) return [];

  if ($isDynamicContentNode(node))
    return transformDynamicNode(
      node,
      editor,
      account_id,
      className,
      paperSize,
      emailDesign,
      pollsData,
      columnCount,
    );

  if ($isPollNode(node))
    return transformPollNode(node, account_id, pollsData[node.getPollId()], paperSize, emailDesign);

  if ($isVideoNode(node)) return transformImage(node, columnCount, paperSize);

  throw new Error(`Unknown node type: ${node.getType()}`);
};

const transformDividerNode = (node: DividerNode): React.JSX.Element => {
  if (!node.__divider)
    return <MjmlDivider key={node.__key} borderWidth="1px" borderColor="#777777" />;

  return <MjmlDivider key={node.__key} mjmlClass={`${node.__divider.type}_divider`} />;
};

const transformPollNode = (
  node: PollNode,
  account_id: UUID,
  poll?: PollWithOptions,
  paperSize: number = 1000,
  emailDesign?: EmailDesign,
): React.JSX.Element => {
  if (!poll) throw new Error("Poll not found");

  const align =
    node.__format === "center" ? "center" : node.__format === "right" ? "right" : "left";

  const answerType = node.getAnswerType();

  if (answerType.type === "custom") {
    return transformCustomPollNode(
      node,
      answerType,
      poll,
      account_id,
      paperSize,
      emailDesign?.button_settings,
    );
  }

  return (
    <Fragment key={node.getKey()}>
      <MjmlSection mjmlClass="section" key={node.getKey() + "-section"}>
        <MjmlColumn>
          <MjmlText>
            <p>
              <strong>{poll.poll.question}</strong>
            </p>
          </MjmlText>
        </MjmlColumn>
      </MjmlSection>
      <StandardPollButtons align={align} options={poll.poll.options} account_id={account_id} />
      {poll.poll.helper_text && (
        <MjmlSection mjmlClass="section" key={node.getKey() + "-section"}>
          <MjmlColumn>
            <MjmlText>
              <p style={{ fontSize: `${(emailDesign?.text_settings?.font_size ?? 16) * 0.8}px` }}>
                {poll.poll.helper_text}
              </p>
            </MjmlText>
          </MjmlColumn>
        </MjmlSection>
      )}
    </Fragment>
  );
};

const transformCustomPollNode = (
  node: PollNode,
  answer_type: Extract<PollSchema["answer_type"], { type: "custom" }>,
  poll: PollWithOptions,
  account_id: UUID,
  paperSize: number = 1000,
  buttonSettings?: EmailDesign["button_settings"],
): React.JSX.Element => {
  const align =
    node.__format === "center" ? "center" : node.__format === "right" ? "right" : "left";

  const buttonWidth = calculateButtonWidthForPoll(
    poll.poll.options,
    paperSize,
    buttonSettings?.[answer_type.button_type] ?? { font_size: 16, border: 1 },
  );

  return (
    <Fragment key={node.getKey()}>
      <MjmlSection mjmlClass="section" key={node.getKey() + "-section"}>
        <MjmlColumn>
          <MjmlText>
            <p>
              <strong>{poll.poll.question}</strong>
            </p>
          </MjmlText>
        </MjmlColumn>
      </MjmlSection>
      {answer_type.alignment === "horizontal" ? (
        <HorizontalPollButtons
          buttonType={answer_type.button_type}
          options={poll.poll.options}
          account_id={account_id}
          align={align}
          buttonWidth={buttonWidth}
          paperSize={paperSize}
        />
      ) : (
        <VerticalPollButtons
          buttonType={answer_type.button_type}
          options={poll.poll.options}
          account_id={account_id}
          align={align}
          buttonWidth={buttonWidth}
          paperSize={paperSize}
        />
      )}
    </Fragment>
  );
};

const transformDynamicNode = (
  node: DynamicContentNode,
  editor: LexicalEditor,
  account_id: UUID,
  className: BodyClassName,
  paperSize?: number,
  emailDesign?: EmailDesign,
  pollsData: { [key: string]: PollWithOptions } = {},
  columnCount: number = 1,
): React.JSX.Element => {
  return (
    <Fragment key={node.__key}>
      <MjmlRaw
        key={node.__key + "-open"}
        dangerouslySetInnerHTML={{
          __html: `{{#if ${node.getCleanSegmentId()}}}`,
        }}
      />
      {node
        .getChildren()
        .map((child) =>
          transfomTopLevel(
            child,
            editor,
            account_id,
            className,
            paperSize,
            emailDesign,
            pollsData,
            columnCount,
          ),
        )}

      <MjmlRaw key={node.__key + "-close"} dangerouslySetInnerHTML={{ __html: `{{/if}}` }} />
    </Fragment>
  );
};

const transformDataTableNode = (
  tableNode: DataTableNode,
  editor: LexicalEditor,
): React.JSX.Element | React.JSX.Element[] => {
  return (
    <MjmlColumn key={tableNode.__key}>
      <MjmlTable key={tableNode.__key}>
        {tableNode.getChildren().map((row) => {
          return $isTableRowNode(row) ? (
            <tr key={row.__key}>
              {row
                .getChildren()
                .filter($isDataTableCellNode)
                .map((cell) => (
                  <td
                    key={cell.__key}
                    style={{ border: tableNode.__border, padding: tableNode.__cellPadding }}
                  >
                    {cell.getChildren().map((list) => {
                      if ($isListNode(list)) {
                        return (
                          <ul
                            key={tableNode.__key}
                            dangerouslySetInnerHTML={{
                              __html: extractListItems(list, editor),
                            }}
                          />
                        );
                      }

                      if ($isParagraphNode(list) || $isHeadingNode(list)) {
                        return (
                          <p
                            key={tableNode.__key}
                            dangerouslySetInnerHTML={{ __html: exportHtml(editor, list) }}
                          />
                        );
                      }

                      return null;
                    })}
                  </td>
                ))}
            </tr>
          ) : null;
        })}
      </MjmlTable>
    </MjmlColumn>
  );
};

const transformTableNode = (
  node: TableNode,
  editor: LexicalEditor,
  account_id: UUID,
  className: BodyClassName,
  emailDesign?: EmailDesign,
  pollsData: { [key: string]: PollWithOptions } = {},
  paperSize?: number,
): React.JSX.Element | React.JSX.Element[] => {
  const cells = node
    .getChildren()
    .filter($isTableRowNode)
    .flatMap((x) => x.getChildren())
    .filter($isTableCellNode);

  const columnPercentageWidths = node.getColWidths();

  return (
    <MjmlGroup key={node.__key}>
      {cells.map((x, index) =>
        transformTableCellNode(
          x,
          editor,
          account_id,
          className,
          cells.length,
          emailDesign,
          pollsData,
          paperSize,
          columnPercentageWidths?.[index],
        ),
      )}
    </MjmlGroup>
  );
};

const transformTableCellNode = (
  node: TableCellNode,
  editor: LexicalEditor,
  account_id: UUID,
  className: BodyClassName,
  groupSize: number,
  emailDesign?: EmailDesign,
  pollsData: { [key: string]: PollWithOptions } = {},
  paperSize?: number,
  percentageWidth?: number,
): React.JSX.Element => {
  const children = node
    .getChildren()
    .map((x) =>
      transfomTopLevel(
        x,
        editor,
        account_id,
        className,
        paperSize,
        emailDesign,
        pollsData,
        groupSize,
      ),
    );

  let props: IMjmlColumnProps = {
    ...(node.__width ? { width: `${node.__width}px` } : {}),
    ...(node.__backgroundColor ? { backgroundColor: node.__backgroundColor } : {}),
  };

  if (typeof percentageWidth === "number") {
    props.width = `${percentageWidth}%`;
  }

  let mjmlClass: string | undefined = "column";

  if ($isStylableTableCellNode(node)) {
    if (node.__custom_padding) {
      mjmlClass = undefined; // with explicit padding, we don't want the column class stylings
      props = {
        ...props,
        paddingTop: node.__custom_padding.top,
        paddingBottom: node.__custom_padding.bottom,
        paddingLeft: node.__custom_padding.left,
        paddingRight: node.__custom_padding.right,
      };
    }
  }

  // The actual properties of the column class are set from the DB
  // So if the user has not re-saved their email tempplate, the db will still
  // have the previous class styles. Therefore we do not add the class name.

  return (
    <MjmlColumn
      mjmlClass={className !== "header" ? mjmlClass : undefined}
      key={node.__key}
      {...props}
    >
      {children}
    </MjmlColumn>
  );
};

const transformDesignHuddle = (
  node: DesignHuddleNode,
  columnCount: number = 1,
  paperSize: number = 600,
): React.JSX.Element => {
  if (!node.__id) return <></>;

  const widthPercent = (node.__proportionOfMaxWidth ?? 1) * 100;
  const pixelWidth = (paperSize / 100) * (1 / columnCount) * widthPercent;

  const url = node.getDesignHuddlePreviewUrl() || node.getImageUrl();
  const href = node.getHref();

  return (
    <MjmlText
      key={node.__key}
      align={node.__format === "center" ? "center" : node.__format === "right" ? "right" : "left"}
      padding={0}
      fontSize={0}
    >
      <ConditionalWrapper condition={!!href} wrapper={(children) => <a href={href}>{children}</a>}>
        <img
          src={url}
          alt=""
          width={pixelWidth}
          loading="lazy"
          style={{
            width: `${pixelWidth}px`,
            maxWidth: `${widthPercent}%`,
            aspectRatio: node.__aspect,
          }}
        />
      </ConditionalWrapper>
    </MjmlText>
  );
};

const transformButton = (node: StylableButtonNode): React.JSX.Element => {
  const props: IMjmlButtonProps = {
    href: node.__url,
    align: node.__format === "center" ? "center" : node.__format === "right" ? "right" : "left",
  };

  if (node.__fullWidth) {
    return (
      <MjmlButton
        mjmlClass={node.__buttonType}
        cssClass="btn-full-width"
        width="100%"
        key={node.__key}
        {...props}
      >
        {node.__text}
      </MjmlButton>
    );
  }

  return (
    <MjmlButton mjmlClass={node.__buttonType} key={node.__key} {...props}>
      {node.__text}
    </MjmlButton>
  );
};

const transformImage = (
  node: ImageNode | VideoNode,
  columnCount: number = 1,
  paperSize: number = 600,
): React.JSX.Element => {
  const widthPercent = (node.__proportionOfMaxWidth ?? 1) * 100;
  const pixelWidth = (paperSize / 100) * (1 / columnCount) * widthPercent;
  const url = node instanceof VideoNode ? node.__videoUrl : node.getHref();
  const imageUrl = node instanceof VideoNode ? node.__thumbnailUrl : node.__url;

  return (
    <MjmlText
      key={node.__key}
      align={node.__format === "center" ? "center" : node.__format === "right" ? "right" : "left"}
      padding={0}
      fontSize={0}
    >
      <ConditionalWrapper condition={!!url} wrapper={(children) => <a href={url}>{children}</a>}>
        <img
          src={imageUrl}
          alt=""
          width={pixelWidth}
          loading="lazy"
          style={{
            width: `${pixelWidth}px`,
            maxWidth: `${widthPercent}%`,
            aspectRatio: node.__aspect,
          }}
        />
      </ConditionalWrapper>
    </MjmlText>
  );
};

const transformText = (
  node: ParagraphNode | HeadingNode | TextNode | ListItemNode,
  editor: LexicalEditor,
): React.JSX.Element => {
  return (
    // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
    <MjmlText key={node.__key} dangerouslySetInnerHTML={{ __html: exportHtml(editor, node) }} />
  );
};

const transformList = (node: ListNode, editor: LexicalEditor): React.JSX.Element => {
  const listItems = extractListItems(node, editor);

  const props = {
    // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
    dangerouslySetInnerHTML: { __html: listItems },
  };
  return (
    <MjmlText key={node.getKey()}>
      {node.getListType() === "number" ? <ol {...props}></ol> : <ul {...props}></ul>}
    </MjmlText>
  );
};

const extractListItems = (node: ListNode, editor: LexicalEditor, nestLevel = 1): string => {
  const list = node.getChildren<ListItemNode>().map((listItemNode: ListItemNode) => {
    const childNodes = listItemNode.getChildren();
    if (childNodes.length === 1 && $isListNode(childNodes[0])) {
      const props = {
        className: `nestedlist_${nestLevel}`,
        // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
        dangerouslySetInnerHTML: { __html: extractListItems(childNodes[0], editor, nestLevel + 1) },
      };
      return ReactDOMServer.renderToString(
        node.getListType() === "number" ? <ol {...props}></ol> : <ul {...props}></ul>,
      );
    }

    return exportHtml(editor, listItemNode);
  });

  return list.join("");
};
